import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useSearchParams } from "react-router-dom";
import { useDebounceSearchTextFn } from '../../../common/hooks/use-debounce.hook';
import thankYouService from '../../../Services/thank-you.service';
import { ThankyouDeleteReason } from "../../../common/enums/thankyou-delete-reason";
import { SidebarLayoutHeader } from '../../../shared/components/SidebarLayoutHeader';
import { Pagination } from '../../shared/Pagination';
import { formatDateForTable } from '../../../common/utils/date.util';
import { useLoaderStore } from '../../../stores/loader.store';
import { ConfirmationModal } from '../../../shared/components/Modal/ConfirmationModal';
import { useSetBreadcrumbs } from '../../../shared/hooks/use-set-breadcrumbs.hook';
import { Modal } from '../../../shared/components/Modal/Modal';
import { DeleteConfirmationModalWithReason } from '../../../shared/components/Modal/DeleteConfiramtionModalWithReason';
import { EditModerationFailedThankYou } from './EditModerationFailedThankYou';
import { ReleaseModerationFailedThankYou } from './ReleaseModerationFailedThankYou';
import moderationFailedThankYouService from '../../../Services/moderation-failed-thankyou.service';
import { downloadCsvFromData, generateCsvFileName } from '../../../common/utils/csv.util';
import { highlightThankyouMessageMatchInRed } from '../../../common/utils/text-highlight';
import { createSearchParamsFromListState, getListStateFromSearchParams } from '../../../utils/list-state.util';
import { ThankYouListFilters } from '../../ThankYou/ThankYouListFilters';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants/pagination.constant';

const MODERATION_FAILED_MESSAGE_ACTION = Object.freeze({
    DELETE: "DELETE",
    EDIT: "EDIT",
    RELEASE: "RELEASE"
});

const singleFilters = ['organizationId', 'platformFlow', 'dateFilterType', 'dateRangeStart', 'dateRangeEnd'];
const multiFilters = ['teamIds'];

/**
 * @typedef SelectedRow
 * @property {string} moderationFailedMessage
 * @property {keyof typeof MODERATION_FAILED_MESSAGE_ACTION} action
 */

export function ModerationFailedThankYouList() {
    const setShowLoader = useLoaderStore((state) => state.setShowLoader);
    const [draftThankYous, setDraftThankYous] = useState([]);
    const [resultsCount, setResultsCount] = useState(0);
    const [deleteReason, setDeleteReason] = useState(Object.keys(ThankyouDeleteReason)[0]);
    const [filterData, setFilterData] = useState('notReleased');
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [showFilters, setShowFilters] = useState(false);

    /** @type {[SelectedRow, React.Dispatch<SelectedRow>]} */
    const [selectedRow, setSelectedRow] = useState(null);

    const state = useMemo(() => getThankYouListStateFromSearchParams(searchParams), [searchParams]);
    const filters = state.filters;
    const pagination = { pageNumber: state.pageNumber, pageSize: DEFAULT_PAGE_SIZE };


    useSetBreadcrumbs([{ label: 'Moderation Failed Messages', path: '' }]);

    useDebounceSearchTextFn(searchText, (debouncedSearchText) => {
        if (debouncedSearchText !== state.searchText) {
            setSearchParams(createThankYouListSearchParams(filters, debouncedSearchText, 1));
        }
    });

    useEffect(() => {
        getThankYous();
        setIsFirstRender(false)
    }, [searchParams]);

    async function getThankYous() {
        setShowLoader(true);
        try {
            const res = await thankYouService.getModerationFailedThankyous({
                search: state.searchText ? state.searchText.trim() : '',
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                status: filterData,
                platformFlow: filters.platformFlow,
                organizationIds: filters.organizationId ? [filters.organizationId] : [],
                teamIds: filters.teamIds,
                dateRangeStart: filters.dateFilterType === "range" ? filters.dateRangeStart : undefined,
                dateRangeEnd: filters.dateFilterType === "range" ? filters.dateRangeEnd : undefined,

            });

            if (res.status === 200) {
                let messages = res.data.results;

                for (let i = 0; i < messages.length; i++) {
                    let item = messages[i];
                    if (item.failedReason) {
                        let highlightData = highlightThankyouMessageMatchInRed(JSON.parse(JSON.stringify(item.description)), JSON.parse(JSON.stringify(item.createdUserData)), item.failedReason);
                        item.descriptionHighlighted = highlightData.message;
                        item.createdUserDataHighlighted = highlightData.contact;
                    }
                    else {
                        item.descriptionHighlighted = item.description;
                        item.createdUserDataHighlighted = item.createdUserData;
                    }
                }
                // highlightMatchInRed(messages.description);
                setDraftThankYous(messages);
                setResultsCount(res.data.count);

                return;
            }

            toast.error("Couldn't get thank yous");
        } catch (err) {
            toast.error("Couldn't get thank yous");
        } finally {
            setShowLoader(false);
        }
    }

    function onPaginationChange(pageNumber) {
        setSearchParams(createThankYouListSearchParams(filters, state.searchText, pageNumber));
    }

    async function handleDeleteMessage() {
        try {
            const moderationFailedMessage = selectedRow.moderationFailedMessage;
            const res = await thankYouService.deleteModerationFailedThankyou(moderationFailedMessage._id, deleteReason);

            if (res.status === 200) {
                toast.success("Successfully deleted message");
                setDraftThankYous((current) => current.filter((data) => data._id !== moderationFailedMessage._id));
                return;
            }

            toast.error("Couldn't delete message");
        } catch (err) {
            console.log(err);
            toast.error("Couldn't delete message");
        } finally {
            setSelectedRow(null);
        }
    }

    async function downloadAsCsv() {
        setShowLoader(true);

        try {
            const res = await moderationFailedThankYouService.downloadAsCsv({});

            const csvName = generateCsvFileName('moderation-failed-thankyous');
            downloadCsvFromData(res, csvName);
        } catch (err) {
            console.log(err);
            toast.error("Couldn't download csv");
        } finally {
            setShowLoader(false);
        }
    }

    const handleDeleteInputChange = (e) => {
        const { name, value } = e.target;
        setDeleteReason(value);
    };

    function createThankYouListSearchParams(filters, searchText, pageNumber) {
        return createSearchParamsFromListState(filters, searchText, pageNumber, singleFilters, multiFilters);
    }

    function getThankYouListStateFromSearchParams(searchParams) {
        return getListStateFromSearchParams(searchParams, singleFilters, multiFilters);
    }

    const handleApplyFilters = (newFilters) => {
        setShowFilters(false);
        setSearchParams(createThankYouListSearchParams(newFilters, state.searchText));
    }

    const extractReason = (thankYou) => {
        if (thankYou?.failedReason) {
            const reasons = thankYou?.failedReason?.map(failedReason => {
                return failedReason?.reasons?.map(reason => reason.reason)[0] ?? ""
            }) ?? "";
            return reasons.join(", ")
        }
    }
    
    return (
        <>
            <main className="page-content bg-light">
                <SidebarLayoutHeader title="Failed Thank you List" />

                <div className="container-fluid">
                    <div className="layout-specing">
                        <div className="card border-0 shadow rounded">
                            <div className='d-flex justify-content-end align-items-center p-2'>
                                <div className="mr-2">
                                    <i className="fi fi-rr-search cstm-search-ro"></i>
                                    <input
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        type="text"
                                        className="cstm-input-seacrh"
                                        placeholder="Search"
                                    />
                                </div>
                                <button type='button' className='custom-filter-icon-btn mr-2' onClick={() => setShowFilters(true)}>
                                    <i className="fi fi-rr-settings-sliders"></i>
                                </button>
                                <div className="">
                                    <button
                                        className='cstm-btn lh-base ml-2'
                                        onClick={(e) => setFilterData('released')}
                                    >
                                        Released Thankyous
                                    </button>
                                </div>
                                <div>
                                    <button
                                        type='button'
                                        className="cstm-eye m-0"
                                        onClick={downloadAsCsv}
                                    >
                                        <i className="fi fi-rr-download"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="col-md-12 col-lg-12">
                                <table className="table mb-0 table-center">
                                    <thead>
                                        <tr className="border-top">
                                            <th className="border-bottom w-4 cstm-userheading">Message</th>
                                            <th className="border-bottom w-4 cstm-userheading">From</th>
                                            <th className="border-bottom w-4 cstm-userheading">To</th>
                                            <th className="border-bottom w-4 cstm-userheading">Team</th>
                                            <th className="border-bottom w-4 cstm-userheading">Reason</th>
                                            <th className="border-bottom w-4 cstm-userheading">Created Date</th>
                                            <th className="border-bottom w-4 cstm-userheading">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {draftThankYous?.map((thankYou) => {
                                            return (
                                                <tr
                                                    key={thankYou._id}
                                                    className="cstm-Tabledesign cstm-usertable-design"
                                                >
                                                    <td><p style={{ color: "black" }} dangerouslySetInnerHTML={{ __html: thankYou.descriptionHighlighted }} /></td>
                                                    <td>
                                                        <div><p style={{ color: "black" }} dangerouslySetInnerHTML={{ __html: thankYou.createdUserDataHighlighted?.firstName }} /></div>
                                                        <div><p style={{ color: "black" }} dangerouslySetInnerHTML={{ __html: thankYou.createdUserDataHighlighted?.email }} /></div>
                                                        <div>{thankYou.createdUserData?.phoneNumber}</div>
                                                    </td>
                                                    <td>
                                                        <div>{thankYou.users ? thankYou.users[0]?.firstName : null}</div>
                                                        <div>{thankYou.users ? thankYou.users[0]?.email : null}</div>
                                                        <div>{thankYou.users ? thankYou.users[0]?.phoneNumber : null}</div>
                                                    </td>
                                                    <td>
                                                        <div>{thankYou.teams && thankYou.teams[0] ? thankYou.teams[0]?.name : 'N/A'}</div>
                                                    </td>
                                                    <td>
                                                        <div>{thankYou.failedReason ? extractReason(thankYou) : 'N/A'}</div>
                                                    </td>
                                                    <td>{thankYou.createdAt ? formatDateForTable(thankYou.createdAt) : null}</td>
                                                    <td>
                                                        <div>
                                                            <Link
                                                                to={""}
                                                                onClick={() => setSelectedRow({ moderationFailedMessage: thankYou, action: MODERATION_FAILED_MESSAGE_ACTION.EDIT })}
                                                                className="cstm-eye"
                                                            >
                                                                <i className="fi fi-rr-pencil"></i>
                                                            </Link>
                                                            <Link
                                                                to={""}
                                                                onClick={() => setSelectedRow({ moderationFailedMessage: thankYou, action: MODERATION_FAILED_MESSAGE_ACTION.DELETE })}
                                                                className="cstm-delete mrn-rt"
                                                            >
                                                                <i className="fi fi-rr-trash"></i>
                                                            </Link>

                                                            {thankYou.isReleased ? 'Released' :
                                                                <button
                                                                    type='button'
                                                                    className="cstm-eye"
                                                                    onClick={() => setSelectedRow({ moderationFailedMessage: thankYou, action: MODERATION_FAILED_MESSAGE_ACTION.RELEASE })}
                                                                    class="cstm-delete mrn-rt"
                                                                >
                                                                    <i className="fi fi-rr-cloud-upload"></i>

                                                                </button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {!draftThankYous?.length && (
                                    <div className="NoRecord-cstm">No records found!</div>
                                )}
                            </div>
                        </div>

                        <Pagination
                            totalRows={resultsCount}
                            currentPage={pagination.pageNumber}
                            rowsPerPage={pagination.pageSize}
                            onPaginationChange={onPaginationChange}
                        />
                    </div>
                </div>
            </main>

            {(selectedRow && selectedRow.action === MODERATION_FAILED_MESSAGE_ACTION.DELETE) && (
                <DeleteConfirmationModalWithReason
                    text='Are you sure you want to delete this message?'
                    confirmBtnText='Delete'
                    type='delete'
                    onConfirm={handleDeleteMessage}
                    onCancel={() => setSelectedRow(null)}
                    reasonValue={deleteReason}
                    handleDeleteInputChange={handleDeleteInputChange}
                />
            )}

            {(selectedRow && selectedRow.action === MODERATION_FAILED_MESSAGE_ACTION.EDIT) && (
                <Modal
                    show={true}
                    onClose={() => setSelectedRow(null)}
                >
                    <EditModerationFailedThankYou
                        moderationFailedThankyou={selectedRow.moderationFailedMessage}
                        onEditSuccess={() => {
                            setSelectedRow(null);
                            getThankYous(pagination.pageNumber, pagination.pageSize);
                        }}
                    />
                </Modal>
            )}

            {(selectedRow && selectedRow.action === MODERATION_FAILED_MESSAGE_ACTION.RELEASE) && (
                <Modal
                    show={true}
                    onClose={() => setSelectedRow(null)}
                >
                    <ReleaseModerationFailedThankYou
                        moderationFailedThankyou={selectedRow.moderationFailedMessage}
                        onReleaseSuccess={() => {
                            setSelectedRow(null);
                        }}
                    />
                </Modal>
            )}

            <Modal
                show={showFilters}
                onClose={() => setShowFilters(false)}
            >
                <ThankYouListFilters
                    filters={filters}
                    onApplyFilters={handleApplyFilters}
                />
            </Modal>
        </>
    );
}